import React, {
    useState,
    useEffect,
    createContext,
    useContext,
    useCallback,
    useRef,
} from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL;

// Axios instance that automatically attaches the Bearer token
export const authAxios = axios.create({ baseURL: API });

/** Auth headers for API requests (CloudFront strips Authorization; X-Access-Token is forwarded). */
export function authRequestHeaders(token) {
    if (!token) return {};
    return {
        Authorization: `Bearer ${token}`,
        'X-Access-Token': token,
    };
}

function isAuthErrorResponse(status, detail) {
    if (status === 401) return true;
    return typeof detail === 'string' && detail.startsWith('auth_failed:');
}

export function AuthProvider({ children }) {
    // Access token lives only in memory (not localStorage) for security
    const [accessToken, setAccessToken] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const accessTokenRef = useRef(null);

    const applyAccessToken = useCallback((token) => {
        accessTokenRef.current = token;
        setAccessToken(token);
    }, []);

    const storeSession = useCallback((tokens) => {
        applyAccessToken(tokens.access_token);
        setUser(tokens.user);
        localStorage.setItem('hp_refresh_token', tokens.refresh_token);
    }, [applyAccessToken]);

    const clearSession = useCallback(() => {
        applyAccessToken(null);
        setUser(null);
        localStorage.removeItem('hp_refresh_token');
    }, [applyAccessToken]);

    /** Returns the new access token, or null if refresh failed. */
    const refreshSession = useCallback(async () => {
        const refreshToken = localStorage.getItem('hp_refresh_token');
        if (!refreshToken) {
            return null;
        }
        try {
            const { data } = await axios.post(`${API}/auth/refresh`, {
                refresh_token: refreshToken,
            });
            storeSession(data);
            return data.access_token;
        } catch (err) {
            console.warn('[auth] refresh failed', err.response?.status, err.response?.data);
            clearSession();
            return null;
        }
    }, [storeSession, clearSession]);

    /** Returns a valid access token, refreshing the session if needed. */
    const ensureAccessToken = useCallback(async () => {
        if (accessTokenRef.current) {
            return accessTokenRef.current;
        }
        return refreshSession();
    }, [refreshSession]);

    // Restore session on mount (page reload)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            await refreshSession();
            if (!cancelled) {
                setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [refreshSession]);

    // Attach interceptors once; always read the latest token from the ref
    useEffect(() => {
        const requestInterceptor = authAxios.interceptors.request.use((config) => {
            const token = accessTokenRef.current;
            if (token) {
                Object.assign(config.headers, authRequestHeaders(token));
            }
            return config;
        });

        const responseInterceptor = authAxios.interceptors.response.use(
            (res) => res,
            async (error) => {
                const status = error.response?.status;
                const detail = error.response?.data?.detail;
                const shouldRetryAuth =
                    isAuthErrorResponse(status, detail) && !error.config._retry;

                if (shouldRetryAuth) {
                    error.config._retry = true;
                    const token = await refreshSession();
                    if (token) {
                        Object.assign(error.config.headers, authRequestHeaders(token));
                        return authAxios(error.config);
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            authAxios.interceptors.request.eject(requestInterceptor);
            authAxios.interceptors.response.eject(responseInterceptor);
        };
    }, [refreshSession]);

    const login = async (email, password) => {
        const { data } = await axios.post(`${API}/auth/login`, { email, password });
        storeSession(data);
        setLoading(false);
        return data.user;
    };

    const register = async (email, password, firstName, lastName) => {
        const { data } = await axios.post(`${API}/auth/register`, {
            email,
            password,
            first_name: firstName,
            last_name: lastName,
        });
        storeSession(data);
        setLoading(false);
        return data.user;
    };

    const logout = async () => {
        const refreshToken = localStorage.getItem('hp_refresh_token');
        if (refreshToken) {
            try {
                await axios.post(`${API}/auth/logout`, { refresh_token: refreshToken });
            } catch {
                // best-effort revocation
            }
        }
        clearSession();
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                accessToken,
                loading,
                login,
                register,
                logout,
                refreshSession,
                ensureAccessToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
